import type { MallCategory, MallDetailBlock, MallProduct } from '@/constants/mallProducts'
import type {
  MallProductPayload,
  MallProductStatus,
  ServerMallProduct,
} from '@/api/mallProduct'
import { calcMallUnitPrice } from '@/utils/mallPricing'
import {
  autoUnitText,
  exampleCheckoutPreview,
  formatMinOrderLabel,
  formatOrderQtyRule,
  formatShippingChargeLabel,
  formatStockCount,
  isAutoUnitText,
  needsPackQty,
  normalizeImageShows,
  normalizeOrderStep,
  normalizePackQty,
  normalizeSellUnit,
  normalizeShippingCharge,
  normalizeShippingEvery,
  optionPieceCount,
  sellUnitNoun,
  type MallImageShows,
  type MallSellUnit,
  type MallShippingCharge,
} from '@/utils/mallSell'

/**
 * 상품 편집 폼.
 * 판매업체(등록 요청)와 관리자(검수·보강)가 같은 모양을 쓴다.
 */
export const MAX_PRODUCT_IMAGES = 10
export const MAX_PRODUCT_TAGS = 10

export const LEAD_TIMES = ['당일 출고', '1영업일 이내', '2~3영업일', '주문 제작 · 14일 이내']

/** 원산지 선택. 빈 값은 미입력이다. */
export type OriginKind = '' | 'DOMESTIC' | 'IMPORT' | 'MIXED' | 'OTHER'

export const ORIGIN_KIND_OPTIONS: Array<{ id: OriginKind; label: string }> = [
  { id: '', label: '미입력' },
  { id: 'DOMESTIC', label: '국내산' },
  { id: 'IMPORT', label: '수입산' },
  { id: 'MIXED', label: '국산 및 수입산 혼합' },
  { id: 'OTHER', label: '기타' },
]

/** 농수산물·가공식품·축산물이 들어가는 분류. 이 분류만 원산지가 필수다. */
const ORIGIN_REQUIRED_CATEGORIES = ['식품/건강']

export function originRequiredFor(category: string): boolean {
  return ORIGIN_REQUIRED_CATEGORIES.includes(String(category || '').trim())
}

export function composeOrigin(kind: OriginKind, detail: string): string {
  const note = detail.trim()
  if (kind === 'DOMESTIC') return '국내산'
  if (kind === 'IMPORT') return note ? `수입산 · ${note}` : '수입산'
  if (kind === 'MIXED') return note ? `국산 및 수입산 혼합 · ${note}` : '국산 및 수입산 혼합'
  if (kind === 'OTHER') return note ? `기타 · ${note}` : '기타'
  return ''
}

export function parseOrigin(value: string): { kind: OriginKind; detail: string } {
  const text = String(value || '').trim()
  if (!text) return { kind: '', detail: '' }
  if (text === '국내산') return { kind: 'DOMESTIC', detail: '' }
  if (text.startsWith('수입산')) {
    return { kind: 'IMPORT', detail: text.replace(/^수입산\s*(?:·|:)?\s*/, '').trim() }
  }
  if (text.startsWith('국산 및 수입산 혼합')) {
    return { kind: 'MIXED', detail: text.replace(/^국산 및 수입산 혼합\s*(?:·|:)?\s*/, '').trim() }
  }
  if (text.startsWith('기타')) {
    return { kind: 'OTHER', detail: text.replace(/^기타\s*(?:·|:)?\s*/, '').trim() }
  }
  return { kind: 'OTHER', detail: text }
}

export function writeOrigin(form: { origin: string; originKind: OriginKind; originDetail: string }, raw: string) {
  const parsed = parseOrigin(raw)
  form.originKind = parsed.kind
  form.originDetail = parsed.detail
  form.origin = composeOrigin(parsed.kind, parsed.detail)
}

export function originProblemOf(form: { category: string; originKind: OriginKind; originDetail: string }): string {
  const detail = form.originDetail.trim()
  const text = composeOrigin(form.originKind, detail)
  if (text.length > 200) return '원산지는 200자까지 적을 수 있습니다.'
  if (form.originKind === 'IMPORT' && !detail) return '수입산은 국가명을 입력해 주세요.'
  if (form.originKind === 'MIXED' && !detail) return '혼합은 국가별 원산지를 입력해 주세요.'
  if (form.originKind === 'OTHER' && !detail) return '기타 원산지 내용을 입력해 주세요.'
  if (originRequiredFor(form.category) && !detail && form.originKind !== 'DOMESTIC') {
    return '식품·건강 상품은 원산지를 선택해 주세요.'
  }
  return ''
}

export const BADGE_OPTIONS = [
  { value: '', label: '없음' },
  { value: '특가', label: '특가' },
  { value: '인기', label: '인기' },
  { value: '베스트', label: '베스트' },
  { value: 'NEW', label: 'NEW' },
]

export const BLOCK_LABEL: Record<MallDetailBlock['type'], string> = {
  HEADING: '소제목',
  TEXT: '문단',
  IMAGE: '이미지',
  NOTICE: '안내 문구',
}

/* 카테고리마다 기관이 자주 쓰는 검색어가 다르다. */
export const TAG_SUGGESTIONS: Record<MallCategory, string[]> = {
  위생용품: ['대용량', '일회용', '저자극', '업소용', '무향'],
  '의료/요양': ['요양기관', '어르신', '미끄럼방지', '흡수력', '재활'],
  '식품/건강': ['고단백', '연화식', '개별포장', '영양보충', '무설탕'],
  생활용품: ['다용도', '경제형', '휴대용', '겨울철', '위생관리'],
}

/** 영카트 요약정보처럼, 분류 선택 시 빈 스펙 표에 넣을 기본 항목 */
export const SPEC_TEMPLATES: Record<MallCategory, string[]> = {
  위생용품: ['규격', '포장단위', '재질', '인증', '보관방법'],
  '의료/요양': ['규격', '적용대상', '포장단위', '인증', '주의사항'],
  '식품/건강': ['용량', '포장단위', '원재료', '보관방법', '유통기한안내'],
  생활용품: ['규격', '포장단위', '재질', '용도', '주의사항'],
}

/** 관리자 검수 반려 — 반복 사유 칩 */
export const REJECT_REASON_TEMPLATES = [
  '대표 이미지가 부족하거나 품질이 낮습니다. 선명한 사진을 올려 주세요.',
  '상품명·규격이 불명확합니다. 기관이 비교할 수 있게 적어 주세요.',
  '원가·판매가·마진을 다시 확인해 주세요.',
  '상세설명이 부족합니다. 구성·사용법·주의사항을 보완해 주세요.',
  '옵션·재고가 실제와 맞지 않습니다. 맞춰 수정해 주세요.',
  '요약정보(스펙) 표가 비어 있거나 부정확합니다.',
]

/** 판매자 → 관리자 요청 메모 칩 */
export const REQUEST_NOTE_TEMPLATES = [
  '가격·마진 조정이 필요합니다.',
  '이미지 교체를 요청합니다.',
  '규격·포장단위 표기를 확인해 주세요.',
  '옵션(박스/규격)을 추가했습니다.',
  '기존 상품과 동일 규격의 재등록입니다.',
]

const RECENT_DEFAULTS_KEY = 'hamuri.seller.productRecentDefaults'

export interface ProductRecentDefaults {
  category: MallCategory
  unit: string
  origin: string
  maker: string
  shippingFee: number
  leadTime: string
  marginShare: number
  minOrder: number
  orderStep: number
  sellUnit: MallSellUnit
  imageShows: MallImageShows
  packQty: number
  shippingCharge: MallShippingCharge
  shippingEvery: number
}

export function loadRecentProductDefaults(): ProductRecentDefaults | null {
  try {
    const raw = localStorage.getItem(RECENT_DEFAULTS_KEY)
    if (!raw) return null
    const data = JSON.parse(raw) as Partial<ProductRecentDefaults>
    if (!data.category) return null
    return {
      category: data.category,
      unit: String(data.unit || ''),
      origin: String(data.origin || ''),
      maker: String(data.maker || ''),
      shippingFee: Math.max(0, Number(data.shippingFee) || 0),
      leadTime: String(data.leadTime || ''),
      marginShare: Math.max(0, Math.min(100, Number(data.marginShare) || DEFAULT_MARGIN_SHARE)),
      minOrder: Math.max(1, Number(data.minOrder) || 1),
      orderStep: normalizeOrderStep(data.orderStep),
      sellUnit: normalizeSellUnit(data.sellUnit),
      imageShows: normalizeImageShows(data.imageShows),
      packQty: normalizePackQty(data.packQty),
      shippingCharge: normalizeShippingCharge(data.shippingCharge),
      shippingEvery: normalizeShippingEvery(data.shippingEvery),
    }
  } catch {
    return null
  }
}

export function saveRecentProductDefaults(form: ProductEditorForm) {
  try {
    const payload: ProductRecentDefaults = {
      category: form.category,
      unit: form.unit.trim(),
      origin: composeOrigin(form.originKind, form.originDetail),
      maker: form.maker.trim(),
      shippingFee: Math.max(0, Number(form.shippingFee) || 0),
      leadTime: form.leadTime || '',
      marginShare: Math.max(0, Math.min(100, Number(form.marginShare) || DEFAULT_MARGIN_SHARE)),
      minOrder: Math.max(1, Number(form.minOrder) || 1),
      orderStep: normalizeOrderStep(form.orderStep),
      sellUnit: form.sellUnit,
      imageShows: form.imageShows,
      packQty: form.packQty,
      shippingCharge: form.shippingCharge,
      shippingEvery: form.shippingEvery,
    }
    localStorage.setItem(RECENT_DEFAULTS_KEY, JSON.stringify(payload))
  } catch {
    /* ignore */
  }
}

export function applyRecentProductDefaults(form: ProductEditorForm, recent: ProductRecentDefaults) {
  form.category = recent.category
  form.unit = recent.unit
  writeOrigin(form, recent.origin)
  form.maker = recent.maker
  form.shippingFee = recent.shippingFee
  form.leadTime = recent.leadTime
  form.marginShare = recent.marginShare
  form.minOrder = recent.minOrder
  form.orderStep = normalizeOrderStep(recent.orderStep)
  form.sellUnit = recent.sellUnit
  form.imageShows = recent.imageShows
  form.packQty = recent.packQty
  form.shippingCharge = recent.shippingCharge
  form.shippingEvery = recent.shippingEvery
  if (isAutoUnitText(form.unit)) form.unit = autoUnitText(form)
  applySpecTemplate(form, recent.category)
}

/** 구매자에게 보이는 주문 한도 한 줄 */
export function orderLimitPreview(form: ProductEditorForm): string {
  const stock = Math.max(0, Number(form.stock) || 0)
  const noun = sellUnitNoun(form.sellUnit)
  if (stock <= 0) return `재고 0${noun} · 저장 시 품절로 노출됩니다`
  return `몰 노출 재고 ${formatStockCount(stock, form.sellUnit)} · ${formatOrderQtyRule(form.minOrder, form.orderStep, form.sellUnit)}부터 · 최대 재고까지 주문`
}

/** 체크리스트 → 해당 입력 구간으로 점프 */
export type ChecklistSectionId =
  | 'peditor-sell'
  | 'peditor-cate'
  | 'peditor-basic'
  | 'peditor-summary'
  | 'peditor-price'
  | 'peditor-options'
  | 'peditor-ship'
  | 'peditor-images'
  | 'peditor-detail'

/**
 * 몰 미리보기 하이라이트 존.
 * 입력 포커스 ↔ 카드/상세 미리보기 대응에 쓴다.
 */
export type ProductPreviewZone =
  | 'cate'
  | 'name'
  | 'summary'
  | 'badge'
  | 'desc'
  | 'specs'
  | 'price'
  | 'stock'
  | 'sell'
  | 'options'
  | 'ship'
  | 'gallery'
  | 'detail'
  | 'tags'

/**
 * 편집 단계. 몰의 카드/상세는 「보기」이고, 입력은 상품 vs 배송만 나눈다.
 */
export type ProductEditSurface = 'product' | 'shipping'

/** 상품 단계에서 우측 미리보기 모드 */
export type ProductPreviewMode = 'card' | 'detail'

export interface ProductEditSurfaceDef {
  id: ProductEditSurface
  label: string
  /** 한 줄 설명 */
  where: string
  sections: ChecklistSectionId[]
}

export const PRODUCT_EDIT_SURFACES: ProductEditSurfaceDef[] = [
  {
    id: 'product',
    label: '상품',
    where: '판매 방식·이미지·이름·가격·상세',
    sections: [
      'peditor-sell',
      'peditor-images',
      'peditor-basic',
      'peditor-price',
      'peditor-options',
      'peditor-cate',
      'peditor-summary',
      'peditor-detail',
    ],
  },
  {
    id: 'shipping',
    label: '배송·교환',
    where: '배송비·출고·반품 안내',
    sections: ['peditor-ship'],
  },
]

const SECTION_TO_SURFACE: Record<ChecklistSectionId, ProductEditSurface> = {
  'peditor-sell': 'product',
  'peditor-images': 'product',
  'peditor-basic': 'product',
  'peditor-price': 'product',
  'peditor-options': 'product',
  'peditor-cate': 'product',
  'peditor-summary': 'product',
  'peditor-detail': 'product',
  'peditor-ship': 'shipping',
}

const ZONE_TO_SURFACE: Record<ProductPreviewZone, ProductEditSurface> = {
  gallery: 'product',
  badge: 'product',
  name: 'product',
  price: 'product',
  stock: 'product',
  sell: 'product',
  options: 'product',
  cate: 'product',
  summary: 'product',
  desc: 'product',
  specs: 'product',
  detail: 'product',
  tags: 'product',
  ship: 'shipping',
}

/** 상세 미리보기에만 강조되는 존 (카드에는 없음) */
export const DETAIL_PREVIEW_ZONES: readonly ProductPreviewZone[] = [
  'cate',
  'summary',
  'desc',
  'specs',
  'detail',
  'tags',
  'options',
  'sell',
] as const

export function surfaceFromSection(sectionId: string): ProductEditSurface | '' {
  return SECTION_TO_SURFACE[sectionId as ChecklistSectionId] || ''
}

export function surfaceFromPreviewZone(zone: ProductPreviewZone | ''): ProductEditSurface | '' {
  if (!zone) return ''
  return ZONE_TO_SURFACE[zone] || ''
}

export function surfaceDef(id: ProductEditSurface): ProductEditSurfaceDef {
  return PRODUCT_EDIT_SURFACES.find((s) => s.id === id) || PRODUCT_EDIT_SURFACES[0]
}

/** 목록 카드 미리보기에 실제로 보이는 존 */
export const LIST_CARD_ZONES: readonly ProductPreviewZone[] = [
  'gallery',
  'badge',
  'name',
  'price',
  'stock',
  'sell',
] as const

export function previewModeForZone(zone: ProductPreviewZone | ''): ProductPreviewMode | '' {
  if (!zone || zone === 'ship') return ''
  if ((DETAIL_PREVIEW_ZONES as readonly string[]).includes(zone)) return 'detail'
  if ((LIST_CARD_ZONES as readonly string[]).includes(zone)) return 'card'
  return ''
}

/**
 * 현재 편집 단계·미리보기 모드에 맞춰 강조할 존.
 * 해당 보기에 없으면 빈 값(하이라이트 해제).
 */
export function previewZoneVisibleOn(
  zone: ProductPreviewZone | '',
  surface: ProductEditSurface,
  previewMode: ProductPreviewMode = 'card',
): ProductPreviewZone | '' {
  if (!zone) return ''
  if (surface === 'shipping') return zone === 'ship' ? zone : ''
  if (previewMode === 'card') {
    return (LIST_CARD_ZONES as readonly string[]).includes(zone) ? zone : ''
  }
  /* 상세 미리보기: 배송 존만 제외 */
  return zone === 'ship' ? '' : zone
}

/** @deprecated previewZoneVisibleOn 사용 */
export function previewZoneVisibleOnSurface(
  zone: ProductPreviewZone | '',
  surface: ProductEditSurface,
): ProductPreviewZone | '' {
  return previewZoneVisibleOn(zone, surface, surface === 'shipping' ? 'card' : 'card')
}

const FIELD_SUFFIX_TO_ZONE: Record<string, ProductPreviewZone> = {
  cat: 'cate',
  name: 'name',
  /* unit은 목록 카드에 없음 — 포커스 시 카드 하이라이트 해제 */
  summary: 'summary',
  badge: 'badge',
  desc: 'desc',
  origin: 'desc',
  maker: 'desc',
  cost: 'price',
  price: 'price',
  mrate: 'price',
  list: 'price',
  share: 'price',
  stock: 'stock',
  min: 'sell',
  sell: 'sell',
  imgshow: 'sell',
  pack: 'sell',
  ship: 'ship',
  scharge: 'ship',
  severy: 'ship',
  lead: 'ship',
  return: 'ship',
  cutoff: 'ship',
  policy: 'ship',
  tags: 'tags',
}

const SECTION_TO_ZONE: Record<ChecklistSectionId, ProductPreviewZone> = {
  'peditor-cate': 'cate',
  'peditor-basic': 'name',
  'peditor-summary': 'specs',
  'peditor-sell': 'sell',
  'peditor-price': 'price',
  'peditor-options': 'options',
  'peditor-ship': 'ship',
  'peditor-images': 'gallery',
  'peditor-detail': 'detail',
}

/** 포커스된 컨트롤 id·섹션으로 미리보기 존을 고른다. */
export function previewZoneFromFocus(target: HTMLElement | null, fieldUidPrefix: string): ProductPreviewZone | '' {
  if (!target) return ''
  const id = target.id || ''
  if (id.startsWith(`${fieldUidPrefix}-`)) {
    const suffix = id.slice(fieldUidPrefix.length + 1)
    if (FIELD_SUFFIX_TO_ZONE[suffix]) return FIELD_SUFFIX_TO_ZONE[suffix]
  }
  /* 옵션 블록은 가격 카드 안에 중첩되므로 더 가까운 id를 우선한다 */
  const options = target.closest('#peditor-options') as HTMLElement | null
  if (options) return 'options'
  const section = target.closest('[id^="peditor-"]') as HTMLElement | null
  const sectionId = section?.id as ChecklistSectionId | undefined
  if (sectionId && SECTION_TO_ZONE[sectionId]) return SECTION_TO_ZONE[sectionId]
  return ''
}

export function previewZoneFromSection(sectionId: string): ProductPreviewZone | '' {
  return SECTION_TO_ZONE[sectionId as ChecklistSectionId] || ''
}

/** 미리보기 존 클릭 → 편집기 섹션·필드 */
export function focusTargetFromPreviewZone(zone: ProductPreviewZone | ''): {
  section: ChecklistSectionId
  fieldSuffix: string
} | null {
  switch (zone) {
    case 'cate':
      return { section: 'peditor-cate', fieldSuffix: 'cat' }
    case 'name':
      return { section: 'peditor-basic', fieldSuffix: 'name' }
    case 'summary':
      return { section: 'peditor-basic', fieldSuffix: 'summary' }
    case 'badge':
      return { section: 'peditor-basic', fieldSuffix: 'badge' }
    case 'desc':
      return { section: 'peditor-basic', fieldSuffix: 'desc' }
    case 'specs':
      return { section: 'peditor-summary', fieldSuffix: '' }
    case 'price':
      return { section: 'peditor-price', fieldSuffix: 'price' }
    case 'stock':
      return { section: 'peditor-price', fieldSuffix: 'stock' }
    case 'sell':
      return { section: 'peditor-sell', fieldSuffix: 'sell' }
    case 'options':
      return { section: 'peditor-options', fieldSuffix: '' }
    case 'ship':
      return { section: 'peditor-ship', fieldSuffix: 'ship' }
    case 'gallery':
      return { section: 'peditor-images', fieldSuffix: '' }
    case 'detail':
      return { section: 'peditor-detail', fieldSuffix: '' }
    case 'tags':
      return { section: 'peditor-detail', fieldSuffix: 'tags' }
    default:
      return null
  }
}

export interface ProductCheckItem {
  label: string
  ok: boolean
  section: ChecklistSectionId
}

export interface EditorSpec {
  label: string
  value: string
}

export interface EditorBlock {
  key: number
  type: MallDetailBlock['type']
  text: string
  imageUrl: string
  alt: string
}

export interface EditorOption {
  key: number
  group: string
  name: string
  extra: number
  stock: number
  use: boolean
}

/** 총 마진 중 함우리 몫 기본값(%) — 함우리:판매업체 = 5:5 */
export const DEFAULT_MARGIN_SHARE = 50

export interface ProductEditorForm {
  name: string
  category: MallCategory
  summary: string
  description: string
  unit: string
  origin: string
  /** 미입력이면 빈 값. 국내산·수입산·혼합·기타 */
  originKind: OriginKind
  /** 수입 국가명, 혼합의 국가별 원산지, 기타 내용 */
  originDetail: string
  maker: string
  images: string[]
  listPrice: number
  price: number
  cost: number
  marginShare: number
  stock: number
  minOrder: number
  orderStep: number
  maxOrder: number
  sellUnit: MallSellUnit
  imageShows: MallImageShows
  packQty: number
  shippingCharge: MallShippingCharge
  shippingEvery: number
  badge: string
  shippingFee: number
  freeShipOver: number
  leadTime: string
  specs: EditorSpec[]
  blocks: EditorBlock[]
  options: EditorOption[]
  tags: string[]
  requestNote: string
  /** 상시몰(옵션 추가금) / 공동구매몰(카톤 주문). 기존 상품은 공동구매몰 */
  mallKind: 'REGULAR' | 'SALE'
  /** 상품은 특별할인에 묶지 않는다. 저장값은 항상 빈 문자열 */
  saleCode: string
  /** 환불 배송비 기준 수량. 이 수량마다 1회, 나머지는 올림 */
  returnEvery: number
  /** 환불 배송비. 기준 수량마다 받는 금액 */
  returnFee: number
}

/* 블록·옵션 행을 화면에서 구분하기 위한 일련번호 */
let rowSeq = 0

export function nextRowKey(): number {
  rowSeq += 1
  return rowSeq
}

export function createProductForm(defaults: {
  category: MallCategory
  shippingFee: number
  freeShipOver: number
}): ProductEditorForm {
  return {
    name: '',
    category: defaults.category,
    summary: '',
    description: '',
    unit: '',
    origin: '',
    originKind: '',
    originDetail: '',
    maker: '',
    images: [],
    listPrice: 0,
    price: 0,
    cost: 0,
    marginShare: DEFAULT_MARGIN_SHARE,
    stock: 0,
    minOrder: 1,
    orderStep: 1,
    maxOrder: 0,
    sellUnit: 'EACH',
    imageShows: 'EACH',
    packQty: 1,
    shippingCharge: 'ONCE',
    shippingEvery: 2,
    badge: '',
    shippingFee: defaults.shippingFee,
    freeShipOver: defaults.freeShipOver,
    leadTime: '',
    specs: [],
    blocks: [],
    /* 옵션은 선택. 비우면 저장 시 「기본」 옵션이 자동으로 붙는다. */
    options: [],
    tags: [],
    requestNote: '',
    mallKind: 'REGULAR',
    saleCode: '',
    returnEvery: 10,
    returnFee: 5000,
  }
}

export function hydrateProductForm(form: ProductEditorForm, product: ServerMallProduct) {
  form.name = product.name
  form.category = product.category
  form.summary = product.summary
  form.description = product.description
  form.unit = product.unit
  writeOrigin(form, product.origin)
  form.maker = product.maker
  form.images = [...(product.images ?? [])]
  form.listPrice = product.listPrice
  form.price = product.price
  form.cost = product.cost ?? 0
  form.marginShare = product.marginShare ?? DEFAULT_MARGIN_SHARE
  form.stock = product.stock
  form.minOrder = product.minOrder
  form.orderStep = normalizeOrderStep(product.orderStep)
  form.maxOrder = product.maxOrder
  form.sellUnit = normalizeSellUnit(product.sellUnit)
  form.imageShows = normalizeImageShows(product.imageShows)
  form.packQty = normalizePackQty(product.packQty)
  form.shippingCharge = normalizeShippingCharge(product.shippingCharge)
  form.shippingEvery = normalizeShippingEvery(product.shippingEvery)
  form.badge = product.badge
  form.shippingFee = product.shippingFee
  form.freeShipOver = product.freeShipOver
  form.leadTime = product.leadTime || ''
  form.requestNote = product.requestNote ?? ''
  form.mallKind = product.mallKind === 'REGULAR' ? 'REGULAR' : 'SALE'
  form.saleCode = ''
  form.returnEvery = Math.max(1, Number(product.returnEvery) || 10)
  form.returnFee = Math.max(0, Number(product.returnFee) || 0)
  form.specs = (product.specs ?? []).map((spec) => ({ label: spec.label, value: spec.value }))
  form.tags = [...(product.tags ?? [])]
  form.blocks = (product.blocks ?? []).map((block) => ({
    key: nextRowKey(),
    type: block.type,
    text: block.text,
    imageUrl: block.imageUrl,
    alt: block.alt,
  }))
  form.options = (product.options ?? []).map((option) => ({
    key: nextRowKey(),
    group: option.group,
    name: option.name,
    extra: option.extra,
    stock: option.stock,
    use: option.use,
  }))
  if (form.mallKind === 'SALE') form.packQty = saleCartonPieces(form)
  applySaleCarton(form)
}

/** 공동구매 한 카톤에 들어 있는 개수. 예전 카톤 수량·박스 개수도 여기로 모은다. */
export function saleCartonPieces(input: {
  mallKind?: string
  sellUnit?: unknown
  packQty?: number | null
  orderStep?: number | null
  minOrder?: number | null
}): number {
  const step = normalizeOrderStep(input.orderStep)
  const pack = normalizePackQty(input.packQty)
  const min = Math.max(1, Math.trunc(Number(input.minOrder) || 1))
  if (normalizeSellUnit(input.sellUnit) === 'BOX') return Math.max(1, pack * step)
  if (step > 1) return step
  if (pack > 1) return pack
  return min
}

/** 공동구매는 개·박스를 고르지 않는다. 1카톤 개수가 최소 주문이다. */
export function applySaleCarton(form: ProductEditorForm) {
  if (form.mallKind !== 'SALE') return
  const pieces = Math.max(1, Math.trunc(Number(form.packQty) || 1))
  form.sellUnit = 'EACH'
  form.imageShows = 'EACH'
  form.packQty = pieces
  form.orderStep = pieces
  form.minOrder = pieces
}

/** 내용이 빈 블록은 저장에서도 미리보기에서도 뺀다. */
export function filledBlocks(form: ProductEditorForm): EditorBlock[] {
  return form.blocks.filter((block) =>
    block.type === 'IMAGE' ? Boolean(block.imageUrl) : Boolean(block.text.trim()),
  )
}

export function mainImageOf(form: ProductEditorForm): string {
  return form.images[0] ?? ''
}

export interface MarginBreakdown {
  /** 판매가 - 원가 */
  total: number
  /** 총 마진 ÷ 판매가 */
  rate: number
  /** 총 마진 중 함우리 몫 */
  platform: number
  /** 총 마진 중 판매업체 몫 */
  seller: number
  /** 판매업체가 실제로 받는 금액 (원가 + 판매업체 마진) */
  settlement: number
  /** 원가가 판매가보다 높은 상태 */
  negative: boolean
}

/**
 * 상품 하나의 수익 구조.
 * 총 마진을 함우리와 판매업체가 배분율대로 나눈다.
 */
export function marginOf(input: {
  price: number
  /* 구매자용 응답에는 원가가 없다. 없으면 0으로 두고 마진을 계산하지 않는다. */
  cost?: number
  marginShare?: number
}): MarginBreakdown {
  const price = Math.max(0, input.price || 0)
  const cost = Math.max(0, input.cost || 0)
  const share = Math.min(100, Math.max(0, input.marginShare || 0))

  const total = price - cost
  const platform = total > 0 ? Math.round((total * share) / 100) : 0

  return {
    total,
    rate: price > 0 ? Math.round((total / price) * 1000) / 10 : 0,
    platform,
    seller: total - platform,
    settlement: price - platform,
    negative: total < 0,
  }
}

/** 판매가·원가로 마진율(%) 계산. 판매가 대비 총 마진 비율 */
export function marginRateFromPrice(price: number, cost: number): number {
  const sale = Math.max(0, Number(price) || 0)
  const supply = Math.max(0, Number(cost) || 0)
  if (sale <= 0) return 0
  return Math.round(((sale - supply) / sale) * 1000) / 10
}

/**
 * 원가 + 마진율(%) → 판매가.
 * 마진율은 판매가 대비이므로 sale = cost / (1 - rate/100)
 */
export function salePriceFromMarginRate(cost: number, ratePct: number): number {
  const supply = Math.max(0, Number(cost) || 0)
  const rate = Math.min(99.9, Math.max(0, Number(ratePct) || 0))
  if (rate <= 0) return Math.round(supply)
  if (rate >= 100) return Math.round(supply)
  return Math.max(0, Math.round(supply / (1 - rate / 100)))
}

export function marginOfForm(form: ProductEditorForm): MarginBreakdown {
  return marginOf({ price: form.price, cost: form.cost, marginShare: form.marginShare })
}

/** 정가·판매가·원가·할인·배분을 한눈에 보는 수익 구조 */
export interface MarginInsight {
  listPrice: number
  salePrice: number
  cost: number
  share: number
  productDiscountPct: number
  productDiscountAmount: number
  /** 정가 그대로 팔았을 때 마진 */
  atList: MarginBreakdown
  /** 판매가(카탈로그) 기준 — 실제 기본 마진 */
  atSale: MarginBreakdown
  /** 상품할인(정가→판매가)으로 줄어든 총 마진 */
  cutByProductDiscount: number
}

export function marginInsightOf(input: {
  listPrice?: number
  price: number
  cost?: number
  marginShare?: number
}): MarginInsight {
  const salePrice = Math.max(0, Number(input.price) || 0)
  const listPrice = Math.max(0, Number(input.listPrice) || 0) || salePrice
  const cost = Math.max(0, Number(input.cost) || 0)
  const share = Math.min(100, Math.max(0, Number(input.marginShare) || 0))
  const productDiscountAmount = Math.max(0, listPrice - salePrice)
  const productDiscountPct =
    listPrice > 0 && productDiscountAmount > 0
      ? Math.round((productDiscountAmount / listPrice) * 100)
      : 0
  const atList = marginOf({ price: listPrice, cost, marginShare: share })
  const atSale = marginOf({ price: salePrice, cost, marginShare: share })

  return {
    listPrice,
    salePrice,
    cost,
    share,
    productDiscountPct,
    productDiscountAmount,
    atList,
    atSale,
    cutByProductDiscount: Math.max(0, atList.total - atSale.total),
  }
}

export function marginInsightOfForm(form: ProductEditorForm): MarginInsight {
  return marginInsightOf({
    listPrice: form.listPrice,
    price: form.price,
    cost: form.cost,
    marginShare: form.marginShare,
  })
}

/** 판매가 기준 1개당 마진 (주문 단가와 동일: max(0, unitPrice - cost)) */
export function marginWithOrgDiscount(
  input: { listPrice?: number; price: number; cost?: number; marginShare?: number },
  orgDiscountPct: number,
): {
  unitPrice: number
  productOff: number
  orgOff: number
  margin: MarginBreakdown
} {
  const priced = calcMallUnitPrice({
    listPrice: input.listPrice,
    salePrice: input.price,
    orgDiscountPct,
  })
  const cost = Math.max(0, Number(input.cost) || 0)
  const share = Math.min(100, Math.max(0, Number(input.marginShare) || 0))
  const unitPrice = priced.unitPrice
  const total = Math.max(0, unitPrice - cost)
  const platform = Math.min(unitPrice, Math.max(0, Math.round((total * share) / 100)))

  return {
    unitPrice,
    productOff: priced.productOff,
    orgOff: priced.orgOff,
    margin: {
      total,
      rate: unitPrice > 0 ? Math.round((total / unitPrice) * 1000) / 10 : 0,
      platform,
      seller: total - platform,
      settlement: unitPrice - platform,
      negative: unitPrice - cost < 0,
    },
  }
}

export function shippingLabelOf(form: ProductEditorForm): string {
  return `배송비 ${formatShippingChargeLabel(form)}`
}

export function sellExamplePreview(form: ProductEditorForm): string {
  const extras = form.options.map((option) => Math.max(0, Number(option.extra) || 0))
  const optionExtra = extras.length ? Math.max(...extras) : 0
  return exampleCheckoutPreview({
    price: form.price,
    minOrder: form.minOrder,
    orderStep: form.orderStep,
    sellUnit: form.sellUnit,
    shippingFee: form.shippingFee,
    shippingCharge: form.shippingCharge,
    shippingEvery: form.shippingEvery,
    optionExtra: optionExtra > 0 && optionExtra >= (form.price || 0) * 0.3 ? optionExtra : 0,
  })
}

export function optionExtraWarning(form: ProductEditorForm): string {
  const price = Math.max(0, Number(form.price) || 0)
  if (price <= 0) return ''
  const heavy = form.options.filter((option) => {
    const extra = Math.max(0, Number(option.extra) || 0)
    return extra > 0 && extra >= price
  })
  if (!heavy.length) return ''
  const first = heavy[0]
  const unit = first.extra + price
  return `「${first.name || '옵션'}」 추가금 ${first.extra.toLocaleString('ko-KR')}원이 판매가보다 큽니다. 고객 단가는 ${unit.toLocaleString('ko-KR')}원이 됩니다. 박스 가격은 판매 단위에서 정하세요.`
}

export function ensureSellDefaults(form: ProductEditorForm) {
  form.sellUnit = normalizeSellUnit(form.sellUnit)
  form.imageShows = normalizeImageShows(form.imageShows)
  form.packQty = normalizePackQty(form.packQty)
  form.shippingCharge = normalizeShippingCharge(form.shippingCharge)
  form.shippingEvery = normalizeShippingEvery(form.shippingEvery)
  form.orderStep = normalizeOrderStep(form.orderStep)
  if (needsPackQty(form) && form.packQty < 1) form.packQty = 1
}

export function toProductPayload(form: ProductEditorForm, status: MallProductStatus): MallProductPayload {
  return {
    name: form.name.trim(),
    category: form.category,
    summary: form.summary.trim(),
    description: form.description.trim(),
    unit: form.unit.trim() || autoUnitText(form),
    thumbnail: mainImageOf(form),
    images: [...form.images],
    listPrice: form.price || 0,
    price: form.price || 0,
    cost: Math.max(0, form.cost || 0),
    marginShare: Math.min(100, Math.max(0, form.marginShare ?? DEFAULT_MARGIN_SHARE)),
    stock: form.stock || 0,
    minOrder: form.mallKind === 'SALE' ? Math.max(1, Math.trunc(Number(form.packQty) || 1)) : Math.max(1, form.minOrder || 1),
    orderStep: form.mallKind === 'SALE' ? Math.max(1, Math.trunc(Number(form.packQty) || 1)) : normalizeOrderStep(form.orderStep),
    maxOrder: 0,
    sellUnit: form.mallKind === 'SALE' ? 'EACH' : form.sellUnit,
    imageShows: form.mallKind === 'SALE' ? 'EACH' : form.imageShows,
    packQty: form.mallKind === 'SALE' ? Math.max(1, Math.trunc(Number(form.packQty) || 1)) : needsPackQty(form) ? normalizePackQty(form.packQty) : 1,
    shippingCharge: 'ONCE',
    shippingEvery: 2,
    shippingFee: 0,
    mallKind: form.mallKind === 'REGULAR' ? 'REGULAR' : 'SALE',
    saleCode: '',
    returnEvery: Math.max(1, Number(form.returnEvery) || 10),
    returnFee: Math.max(0, Number(form.returnFee) || 0),
    freeShipOver: 0,
    leadTime: form.leadTime,
    origin: composeOrigin(form.originKind, form.originDetail),
    maker: form.maker.trim(),
    badge: form.badge,
    status,
    requestNote: form.requestNote.trim(),
    blocks: filledBlocks(form).map((block) => ({
      type: block.type,
      text: block.text.trim(),
      imageUrl: block.imageUrl,
      alt: block.alt.trim(),
    })),
    specs: form.specs
      .map((spec) => ({ label: spec.label.trim(), value: spec.value.trim() }))
      .filter((spec) => spec.label && spec.value),
    options: (() => {
      const productStock = Math.max(0, form.stock || 0)
      const list = form.options
        .filter((option) => option.name.trim())
        .map((option) => ({
          group: option.group.trim() || '구성',
          name: option.name.trim(),
          extra: option.extra || 0,
          stock: option.stock || 0,
          use: option.use,
        }))
      /* 옵션을 안 넣으면 「기본」 한 줄로 등록한다 */
      if (!list.length) {
        return [
          {
            group: '구성',
            name: form.unit.trim() || '기본',
            extra: 0,
            stock: productStock,
            use: true,
          },
        ]
      }
      /* 옵션 재고를 안 채운 채 상품 재고만 넣은 경우 → 단일 옵션에 상품 재고를 맞춘다 */
      if (list.length === 1 && list[0].stock <= 0 && productStock > 0) {
        list[0].stock = productStock
      }
      return withRegularUnitOption(form.mallKind, list)
    })(),
    tags: [...form.tags],
  }
}

/** 상시몰 첫 옵션은 1개 판매가와 같은 1개다. 없으면 앞에 붙인다. */
function withRegularUnitOption<T extends { group: string; name: string; extra: number; stock: number; use: boolean }>(
  mallKind: string,
  options: T[],
): T[] {
  if (mallKind !== 'REGULAR') return options
  const live = options.filter((option) => option.use !== false && option.name.trim())
  const allPieces = live.length > 0 && live.every((option) => optionPieceCount(option.name) != null)
  const groups = new Set(live.map((option) => option.group.trim() || '구성'))
  if (groups.size > 1 && !allPieces) return options
  const unitExisting = live.find((option) => optionPieceCount(option.name) === 1)
  const group = unitExisting?.group.trim() || live[0]?.group.trim() || '구성'
  if (allPieces) {
    live.forEach((option) => {
      option.group = group
    })
  }
  let unit = live.find((option) => optionPieceCount(option.name) === 1)
  if (!unit && live.length === 1 && optionPieceCount(live[0].name) == null && !live[0].extra) {
    live[0].name = '1개'
    live[0].group = group
    live[0].extra = 0
    unit = live[0]
  }
  if (!unit) {
    return [{ group, name: '1개', extra: 0, stock: 0, use: true } as T, ...options]
  }
  unit.name = '1개'
  unit.group = group
  unit.extra = 0
  unit.use = true
  const rest = options.filter((option) => option !== unit && !(option.use !== false && optionPieceCount(option.name) === 1))
  return [unit, ...rest]
}

/** 저장 전에 구매자 화면이 어떻게 보일지 그리기 위한 임시 상품 */
export function toPreviewProduct(
  form: ProductEditorForm,
  meta: {
    code?: string
    storeName?: string
    storeCutoff?: string
    storeReturnFee?: number
    storeReturnPolicy?: string
  } = {},
): MallProduct {
  return {
    id: meta.code || 'PREVIEW',
    name: form.name || '예) 성인용 기저귀 중형 60매',
    category: form.category,
    summary: form.summary,
    description: form.description,
    image: mainImageOf(form),
    images: [...form.images],
    listPrice: form.price,
    price: form.price,
    unit: form.unit,
    badge: form.badge || undefined,
    tags: [...form.tags],
    highlights: form.specs.filter((spec) => spec.label && spec.value),
    rating: 0,
    reviewCount: 0,
    soldCount: 0,
    freeShipping: false,
    minOrder: form.mallKind === 'SALE' ? Math.max(1, Math.trunc(Number(form.packQty) || 1)) : form.minOrder,
    orderStep: form.mallKind === 'SALE' ? Math.max(1, Math.trunc(Number(form.packQty) || 1)) : form.orderStep,
    mallKind: form.mallKind === 'REGULAR' ? 'REGULAR' : 'SALE',
    sellUnit: form.mallKind === 'SALE' ? 'EACH' : form.sellUnit,
    imageShows: form.mallKind === 'SALE' ? 'EACH' : form.imageShows,
    packQty: form.mallKind === 'SALE' ? Math.max(1, Math.trunc(Number(form.packQty) || 1)) : form.packQty,
    shippingCharge: form.shippingCharge,
    shippingEvery: form.shippingEvery,
    reviews: [],
    storeName: meta.storeName ?? '',
    blocks: filledBlocks(form).map((block) => ({
      type: block.type,
      text: block.text,
      imageUrl: block.imageUrl,
      alt: block.alt,
    })),
    stock: form.stock,
    leadTime: form.leadTime,
    shippingFee: form.shippingFee,
    shippingCharge: form.shippingCharge,
    shippingEvery: form.shippingEvery,
    storeCutoff: meta.storeCutoff,
    storeReturnFee: meta.storeReturnFee,
    storeReturnPolicy: meta.storeReturnPolicy,
    origin: composeOrigin(form.originKind, form.originDetail),
    maker: form.maker,
    options: (() => {
      const productStock = Math.max(0, form.stock || 0)
      const list = form.options
        .filter((option) => option.name.trim())
        .map((option) => ({
          group: option.group.trim() || '구성',
          name: option.name.trim(),
          extra: option.extra || 0,
          stock: option.stock || 0,
          use: option.use,
        }))
      if (!list.length) {
        list.push({
          group: '구성',
          name: form.unit.trim() || '기본',
          extra: 0,
          stock: productStock,
          use: true,
        })
      }
      return withRegularUnitOption(form.mallKind, list)
    })(),
  }
}

/** 판매로 올리기 전에 반드시 채워야 하는 것들 (+ 포커스 위치) */
export interface ProductIssue {
  message: string
  section: ChecklistSectionId
  fieldSuffix: string
}

const MALL_CATEGORY_SET = new Set<string>(['위생용품', '의료/요양', '식품/건강', '생활용품'])

export function productIssues(form: ProductEditorForm): ProductIssue[] {
  const issues: ProductIssue[] = []

  if (form.name.trim().length < 2) {
    issues.push({
      message: '상품명을 2자 이상 입력해 주세요.',
      section: 'peditor-basic',
      fieldSuffix: 'name',
    })
  }
  if (!MALL_CATEGORY_SET.has(String(form.category || '').trim())) {
    issues.push({
      message: '카테고리를 선택해 주세요.',
      section: 'peditor-cate',
      fieldSuffix: 'cat',
    })
  }
  if (form.price <= 0) {
    issues.push({
      message: '판매가를 입력해 주세요.',
      section: 'peditor-price',
      fieldSuffix: 'price',
    })
  }
  if (form.cost <= 0) {
    issues.push({
      message: '공급가를 입력해 주세요. 마진 계산에 필요합니다.',
      section: 'peditor-price',
      fieldSuffix: 'cost',
    })
  }
  if (form.cost > 0 && form.price > 0 && form.cost > form.price) {
    issues.push({
      message: '공급가가 판매가보다 높습니다. 가격을 다시 확인해 주세요.',
      section: 'peditor-price',
      fieldSuffix: 'cost',
    })
  }
  if (!mainImageOf(form)) {
    issues.push({
      message: '상품 이미지를 한 장 이상 올려 주세요.',
      section: 'peditor-images',
      fieldSuffix: '',
    })
  }
  if (form.stock > 0 && form.minOrder > form.stock) {
    issues.push({
      message: `최소 주문이 재고보다 많습니다.`,
      section: 'peditor-sell',
      fieldSuffix: 'min',
    })
  }
  if (needsPackQty(form) && normalizePackQty(form.packQty) < 1) {
    issues.push({
      message: '1박스에 몇 개인지 입력해 주세요.',
      section: 'peditor-sell',
      fieldSuffix: 'pack',
    })
  }
  if (form.shippingCharge === 'PER_N' && normalizeShippingEvery(form.shippingEvery) < 2) {
    issues.push({
      message: '몇 개(박스)마다 배송비를 받을지 2 이상으로 적어 주세요.',
      section: 'peditor-ship',
      fieldSuffix: 'severy',
    })
  }
  const originProblem = originProblemOf(form)
  if (originProblem) {
    issues.push({
      message: originProblem,
      section: 'peditor-cate',
      fieldSuffix: 'origin',
    })
  }
  if (!form.leadTime.trim()) {
    issues.push({
      message: '출고 소요를 입력해 주세요.',
      section: 'peditor-ship',
      fieldSuffix: 'lead',
    })
  } else if (form.leadTime.trim().length > 30) {
    issues.push({
      message: '출고 소요는 30자까지 적을 수 있습니다.',
      section: 'peditor-ship',
      fieldSuffix: 'lead',
    })
  }

  return issues
}

export function productProblems(form: ProductEditorForm): string[] {
  return productIssues(form).map((issue) => issue.message)
}

export function productChecklist(form: ProductEditorForm): ProductCheckItem[] {
  return [
    {
      label: '기본분류를 선택했습니다',
      ok: MALL_CATEGORY_SET.has(String(form.category || '').trim()),
      section: 'peditor-cate',
    },
    {
      label: '상품명을 5자 이상 입력했습니다',
      ok: form.name.trim().length >= 5,
      section: 'peditor-basic',
    },
    {
      label: '판매 방식(개당·박스)을 정했습니다',
      ok: form.sellUnit === 'EACH' || form.sellUnit === 'BOX',
      section: 'peditor-sell',
    },
    {
      label: '판매가·공급가를 맞게 입력했습니다',
      ok: form.price > 0 && form.cost > 0 && form.cost <= form.price,
      section: 'peditor-price',
    },
    {
      label: '최소 주문이 재고 이내입니다',
      ok: !(form.stock > 0 && form.minOrder > form.stock),
      section: 'peditor-sell',
    },
    {
      label: '대표 이미지를 등록했습니다',
      ok: Boolean(mainImageOf(form)),
      section: 'peditor-images',
    },
    {
      label: '상품 소개를 20자 이상 작성했습니다',
      ok: form.description.trim().length >= 20,
      section: 'peditor-basic',
    },
    {
      label: '상세 페이지 블록을 1개 이상 넣었습니다',
      ok: filledBlocks(form).length > 0,
      section: 'peditor-detail',
    },
    {
      label: '해시태그를 1개 이상 넣었습니다',
      ok: form.tags.length > 0,
      section: 'peditor-detail',
    },
  ]
}

/** 영카트 「옵션목록생성」 — 옵션명+항목(콤마)으로 조합 행을 만든다 */
export function parseOptionItems(raw: string): string[] {
  return String(raw || '')
    .split(/[,，、/|]/)
    .map((item) => item.trim())
    .filter(Boolean)
    .slice(0, 40)
}

export function generateOptionRows(
  groups: Array<{ subject: string; itemsRaw: string }>,
  stock = 0,
): EditorOption[] {
  const active = groups
    .map((group) => ({
      subject: group.subject.trim(),
      items: parseOptionItems(group.itemsRaw),
    }))
    .filter((group) => group.subject && group.items.length)
    .slice(0, 2)

  if (!active.length) return []

  if (active.length === 1) {
    return active[0].items.map((name) => ({
      key: nextRowKey(),
      group: active[0].subject,
      name,
      extra: 0,
      stock: Math.max(0, stock),
      use: true,
    }))
  }

  const rows: EditorOption[] = []
  const [first, second] = active
  first.items.forEach((a) => {
    second.items.forEach((b) => {
      rows.push({
        key: nextRowKey(),
        group: `${first.subject}/${second.subject}`,
        name: `${a} / ${b}`,
        extra: 0,
        stock: Math.max(0, stock),
        use: true,
      })
    })
  })
  return rows.slice(0, 80)
}

/** 분류 스펙 템플릿 — 표가 비어 있을 때만 채운다 */
export function applySpecTemplate(form: ProductEditorForm, category: MallCategory): boolean {
  const filled = form.specs.some((spec) => spec.label.trim() || spec.value.trim())
  if (filled) return false
  const labels = SPEC_TEMPLATES[category] || []
  if (!labels.length) return false
  form.specs = labels.map((label) => ({ label, value: '' }))
  return true
}

/** 앵커 네비용 구간 완료 여부 */
export function sectionDoneMap(form: ProductEditorForm): Record<ChecklistSectionId, boolean> {
  const checks = productChecklist(form)
  const basicOk = checks.filter((item) => item.section === 'peditor-basic').every((item) => item.ok)
  const priceOk = checks.filter((item) => item.section === 'peditor-price').every((item) => item.ok)
  const imageOk = checks.filter((item) => item.section === 'peditor-images').every((item) => item.ok)
  const detailOk = checks.filter((item) => item.section === 'peditor-detail').every((item) => item.ok)
  return {
    'peditor-sell': form.sellUnit === 'EACH' || form.sellUnit === 'BOX',
    'peditor-cate': Boolean(form.category),
    'peditor-basic': basicOk,
    'peditor-summary': form.specs.some((spec) => spec.label.trim() && spec.value.trim()),
    'peditor-price': priceOk,
    'peditor-options': true,
    'peditor-ship': Boolean(form.leadTime),
    'peditor-images': imageOk,
    'peditor-detail': detailOk,
  }
}

/** 검수 실패 시 포커스할 첫 필드 — 필수(productIssues) 우선, 그다음 작성 가이드 */
export function firstFocusTarget(form: ProductEditorForm): {
  section: ChecklistSectionId
  fieldSuffix: string
} {
  const hard = productIssues(form)[0]
  if (hard) return { section: hard.section, fieldSuffix: hard.fieldSuffix }

  if (form.name.trim().length < 5) return { section: 'peditor-basic', fieldSuffix: 'name' }
  if (form.description.trim().length < 20) return { section: 'peditor-basic', fieldSuffix: 'desc' }
  if (filledBlocks(form).length === 0) return { section: 'peditor-detail', fieldSuffix: '' }
  if (form.tags.length === 0) return { section: 'peditor-detail', fieldSuffix: 'tags' }

  return { section: 'peditor-price', fieldSuffix: 'price' }
}

const LOCAL_DRAFT_KEY = 'hamuri.seller.productLocalDraft'

export interface LocalProductDraft {
  productCode: string
  savedAt: string
  form: ProductEditorForm
}

export function saveLocalProductDraft(form: ProductEditorForm, productCode = '') {
  try {
    const payload: LocalProductDraft = {
      productCode: productCode || '',
      savedAt: new Date().toISOString(),
      form: JSON.parse(JSON.stringify(form)) as ProductEditorForm,
    }
    localStorage.setItem(LOCAL_DRAFT_KEY, JSON.stringify(payload))
  } catch {
    /* ignore */
  }
}

export function loadLocalProductDraft(): LocalProductDraft | null {
  try {
    const raw = localStorage.getItem(LOCAL_DRAFT_KEY)
    if (!raw) return null
    const data = JSON.parse(raw) as LocalProductDraft
    if (!data?.form) return null
    return data
  } catch {
    return null
  }
}

export function clearLocalProductDraft() {
  try {
    localStorage.removeItem(LOCAL_DRAFT_KEY)
  } catch {
    /* ignore */
  }
}
